import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import TrimContent from '../../utils/TrimContent';

const API_BASE_URL = '/pceapi/threads/unstructured';

const PROJECT_ROLE_LABELS = {
  PROJECT_MANAGER: 'Project Manager',
  DOSSIER_DEV_LEAD: 'Dossier Development Lead',
  DOSSIER_VIEWER: 'Dossier Viewer',
};


export const useLinkedProjects = (threadId, enabled = true) => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !threadId) {
      setProjects([]);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/details/${threadId}`, {
          headers: { Authorization: GetCookie('access_token') },
        });
        if (cancelled) return;
        const data = response.data;
        setProjects(data?.status === 'success' ? (data.data?.linked_projects || []) : []);
      } catch (error) {
        console.error('Failed to load linked projects:', error);
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [threadId, enabled]);

  return { projects, isLoading };
};

export const LinkedProjectNames = ({ projects }) => {
  if (!projects || projects.length === 0) return null;
  return (
    <div className="ev-message-card2 ev-radius-xsmall p-2 px-3 mb-3 w-100">
      <p className="ev-small-p-blue mb-1">
        <i className="bi bi-exclamation-triangle-fill me-1"></i>
        <strong>
          Linked to {projects.length} project{projects.length !== 1 ? 's' : ''}
        </strong>
      </p>
      <p className="ev-small-p-dark mb-0">
        {projects.map((p) => p.name).join(' · ')}
      </p>
    </div>
  );
};

const LinkedProjectsModal = ({ show, onClose, projects = [], threadName }) => {
  if (!show) return null;

  const lockedCount = projects.filter((p) => !p.can_open).length;

  return ReactDOM.createPortal(
    <div className="modal d-block bg-dark bg-opacity-50" style={{ zIndex: 'var(--num-1000000000)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content ev-card-3">

          <div className="d-flex justify-content-between align-items-center modal-header border-0 pb-2 pt-0 pe-0">
            <h6 className="ev-med-p-dark m-0">
              <i className="bi bi-link-45deg me-2"></i>
              Linked Projects ({projects.length})
            </h6>
            <button
              type="button"
              className="ev-btn-text-only-fill ev-fs-25 avatar-tooltip"
              onClick={onClose}
              data-tooltip="Close"
            >
              <i className="bi bi-x"></i>
            </button>
          </div>

          <div className="modal-body p-3 pt-0">
            {threadName && (
              <div className="ev-message-card2 ev-radius-xsmall p-3 py-2 mb-3 w-100">
                <p className="ev-small-p-dark mb-0 text-break">
                  <strong>Data Folder:</strong> {threadName}
                </p>
              </div>
            )}

            {projects.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-link-45deg text-muted ev-fs-40"></i>
                <p className="ev-small-p-blue mt-2 mb-0">
                  This Data Folder is not linked to any project yet.
                </p>
              </div>
            ) : (
              <>
                {/* height:auto keeps a 2-row list from leaving a tall empty
                    well; .scrollable-element alone sets a fixed height. */}
                <div
                  className="scrollable-element"
                  style={{ height: 'auto', maxHeight: 'var(--vh-45)' }}
                >
                  <table className="table-custom1 mt-0 w-100">
                    <thead>
                      <tr>
                        <th><label>Project</label></th>
                        <th style={{ width: '22%' }}><label>Country</label></th>
                        <th style={{ width: '30%' }} className="text-center"><label>Your Role</label></th>
                        <th style={{ width: '44px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project) => (
                        <tr key={project.fabric_id}>
                          <td className="align-middle">
                            <span className="ev-small-p-dark">
                              <TrimContent text={project.name} maxLength={45} />
                            </span>
                          </td>
                          <td className="align-middle">
                            <span className="ev-small-p-dark">
                              {project.country_name || '—'}
                            </span>
                          </td>
                          <td className="align-middle text-center">
                            {project.project_role ? (
                              <span className="ev-message-card1">
                                {PROJECT_ROLE_LABELS[project.project_role] || project.project_role}
                              </span>
                            ) : (
                              <span className="ev-small-p-dark">—</span>
                            )}
                          </td>
                          <td className="align-middle text-center">
                            {!project.can_open && (
                              <span
                                className="avatar-tooltip avatar-tooltip-end"
                                data-tooltip="You don't have access to this project"
                              >
                                <i className="bi bi-lock text-muted"></i>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lockedCount > 0 && (
                  <p className="ev-small-p-dark mb-0 mt-2">
                    <i className="bi bi-lock me-1"></i>
                    {lockedCount} project{lockedCount !== 1 ? 's' : ''} you don&apos;t
                    have access to
                  </p>
                )}
              </>
            )}
          </div>

          <div className="modal-footer border-0 pt-0">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-reg"
              onClick={onClose}
            >
              Close
            </button>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default LinkedProjectsModal;
